import { useParams } from 'react-router-dom'

import { Box, Button, Card, Text } from '@factorialco/deprecated-design-system'

import { useNavigation } from 'modules/core/hooks/useNavigation'
import i18n from 'modules/core/lib/i18n'
import MoneyWithCurrency from 'modules/core/lib/money'

import { Amount } from 'modules/project_management/components/Amount'
import { InsightCard } from 'modules/project_management/components/InsightCard'
import CurrentOrEstimatedProfitTooltipMessage from 'modules/project_management/pages/ProjectDetailsPage/tabs/OverviewPage/FinancialDetailsSection/tooltips/CurrentOrEstimatedProfitTooltipMessage'

interface CurrentProfitInsightProps {
  billableToDate: number
  isProjectFixedCost: boolean
  costToDate: number
  currency: string
  fixedCostPlannedIncome: number
  fixedCostPlannedCents: number | undefined
  locale: string
  timeAndMaterialsPlannedCents: number | undefined
  timeAndMaterialsTotalBudget: number | undefined
}

const CurrentProfitInsight = ({
  billableToDate,
  isProjectFixedCost,
  costToDate,
  currency,
  fixedCostPlannedIncome,
  fixedCostPlannedCents,
  locale,
  timeAndMaterialsPlannedCents,
  timeAndMaterialsTotalBudget,
}: CurrentProfitInsightProps) => {
  const { navigation } = useNavigation()
  const { id: projectId } = useParams()

  // --- Project fixed cost ---
  const fixedCostCurrentProfit = fixedCostPlannedIncome - costToDate
  const fixedCostPlannedProfit =
    fixedCostPlannedCents != null ? fixedCostPlannedIncome - fixedCostPlannedCents : 0
  const fixedCostPercentageProfitCalculation =
    fixedCostPlannedProfit === 0
      ? 0
      : Math.round(
          ((fixedCostCurrentProfit - fixedCostPlannedProfit) / Math.abs(fixedCostPlannedProfit)) *
            100
        )
  const fixedCostPercentageLabel = `${fixedCostPercentageProfitCalculation > 0 ? '+' : ''}${fixedCostPercentageProfitCalculation}%`
  const fixedCostTitle =
    fixedCostPlannedCents != null
      ? i18n.t(
          'project_management.overview.sections.financial_details.insights.current_profit.title_estimated'
        )
      : i18n.t(
          'project_management.overview.sections.financial_details.insights.current_profit.title'
        )

  // --- Time & materials ---
  const timeAndMaterialsCurrentProfit = billableToDate - costToDate
  const timeAndMaterialsTooltipMessage = `(${i18n.t(
    'project_management.overview.sections.financial_details.insights.revenue_to_date.current_label.total_budget'
  )}) - (${i18n.t(
    'project_management.overview.sections.financial_details.insights.cost_to_date.title'
  )})`

  const calculateTimeAndMaterialsPlannedProfit = () => {
    if (timeAndMaterialsTotalBudget != null && timeAndMaterialsPlannedCents != null) {
      const plannedProfit = timeAndMaterialsTotalBudget - timeAndMaterialsPlannedCents
      return plannedProfit === 0 ? undefined : plannedProfit
    }
    return undefined
  }
  const timeAndMaterialsPlannedProfit = calculateTimeAndMaterialsPlannedProfit()

  if (isProjectFixedCost && !fixedCostPlannedIncome) {
    return (
      <Card>
        <Card.Content>
          <Text color="grey1000" size="200" weight="medium" ellipsis whiteSpace="nowrap">
            {fixedCostTitle}
          </Text>
          <Box justifyContent="center" alignItems="center" width="full" paddingY="s24">
            <Box gap="s12" alignItems="center">
              <Text size="300" weight="normal" color="grey800">
                {i18n.t(
                  'project_management.overview.sections.financial_details.insights.revenue_to_date.current_label.add_planned_income.label'
                )}
              </Text>
              <Button.Router
                iconName="add"
                type="secondary"
                to={navigation.projects.id.details.settings.fullPath({ id: projectId ?? '0' })}
              >
                {i18n.t(
                  'project_management.overview.sections.financial_details.insights.revenue_to_date.current_label.add_planned_income.button'
                )}
              </Button.Router>
            </Box>
          </Box>
        </Card.Content>
      </Card>
    )
  }

  if (isProjectFixedCost) {
    return (
      <InsightCard
        info={
          <CurrentOrEstimatedProfitTooltipMessage
            currency={currency}
            plannedCosts={fixedCostPlannedCents ?? 0}
            plannedIncome={fixedCostPlannedIncome}
            costToDate={costToDate}
          />
        }
        titleGap="s6"
        title={fixedCostTitle}
      >
        <Box flexDirection="row" gap="s8" alignItems="center" paddingTop="s6" flexWrap="noWrap">
          <Box flexNoShrink>
            <Amount
              amount={fixedCostCurrentProfit}
              currency={currency}
              size="400"
              weight="semibold"
              color={fixedCostCurrentProfit > 0 ? 'grey1000' : 'moodSuperNegative'}
            />
          </Box>
          <Text size="200" weight="normal" color="grey1000" whiteSpace="nowrap">
            {fixedCostPlannedCents != null
              ? `/ ${MoneyWithCurrency({
                  amount: fixedCostPlannedIncome - fixedCostPlannedCents,
                  currency,
                  locale,
                })}`
              : ''}
          </Text>
        </Box>
        {fixedCostPlannedCents != null && (
          <Box flexDirection="row">
            <Text color="grey800" size="200" weight="semibold" ellipsis whiteSpace="nowrap">
              {fixedCostPercentageLabel}
            </Text>
            <Text color="grey800" size="200" weight="medium" ellipsis whiteSpace="nowrap">
              {'\u00A0'}
              {i18n.t(
                'project_management.overview.sections.financial_details.insights.current_profit.planned_profit_message'
              )}
            </Text>
          </Box>
        )}
      </InsightCard>
    )
  }

  return (
    <InsightCard.Cost
      currency={currency}
      info={timeAndMaterialsTooltipMessage}
      plannedCost={timeAndMaterialsTotalBudget ? timeAndMaterialsPlannedProfit : undefined}
      title={
        timeAndMaterialsPlannedProfit && timeAndMaterialsTotalBudget
          ? i18n.t(
              'project_management.overview.sections.financial_details.insights.current_profit.title_estimated'
            )
          : i18n.t(
              'project_management.overview.sections.financial_details.insights.current_profit.title'
            )
      }
      totalCost={timeAndMaterialsCurrentProfit}
      {...(timeAndMaterialsCurrentProfit < 0 && { color: 'moodSuperNegative' })}
    />
  )
}

export default CurrentProfitInsight
